// Safe Video ID Generator for Mobile
// Avoids memory issues by only using file metadata

/**
 * Generate safe video ID from file metadata only
 * No file content reading - completely safe for mobile
 */
export async function generateSafeVideoId(file: File): Promise<string> {
  try {
    // Create unique identifier from file metadata
    const metadata = {
      name: file.name,
      size: file.size,
      lastModified: file.lastModified,
      type: file.type
    };
    
    const metadataString = JSON.stringify(metadata);
    const encoder = new TextEncoder();
    const data = encoder.encode(metadataString);
    
    // Use SHA-256 for uniqueness
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    // Take first 16 characters for shorter ID
    const shortHash = hashHex.substring(0, 16);
    const timestamp = Date.now().toString(36);
    const randomSuffix = Math.random().toString(36).substr(2, 4);
    
    return `${shortHash}-${timestamp}-${randomSuffix}`;
    
  } catch (error) {
    console.warn('Failed to generate SHA-256-based video ID, falling back to timestamp:', error);
    // Fallback to timestamp-based ID
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

/**
 * Ultra-safe video ID generator (recommended for mobile)
 * Only uses timestamp + random - no crypto operations
 */
export function generateUltraSafeVideoId(): string {
  const timestamp = Date.now().toString(36);
  const random1 = Math.random().toString(36).substr(2, 9);
  const random2 = Math.random().toString(36).substr(2, 9);
  
  return `${timestamp}-${random1}-${random2}`;
}

// utils/videoIdGenerator.ts

// ⚡ fallback mini-hash cho browser cũ (không có crypto.subtle)
function legacyHash(str: string): string {
  let hash = 0, i, chr;
  if (str.length === 0) return "0";
  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit int
  }
  return Math.abs(hash).toString(16);
}

// ⚡ convert ArrayBuffer -> hex string
function bufferToHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
      .map(b => b.toString(16).padStart(2, "0"))
      .join("");
}

/**
 * Generate a smart, stable ID for uploaded videos
 * - Same file = same ID (deterministic)
 * - Reads only small chunks (head + tail), avoids crashing mobile/old browsers
 * - Fallback to legacy hash if crypto.subtle not available
 */
export async function generateSmartVideoId(file: File): Promise<string> {
  const encoder = new TextEncoder();

  // Metadata (ổn định theo file, trên cùng máy)
  const meta = `${file.name}|${file.size}|${file.type}|${file.lastModified}`;

  // Lấy 64KB đầu & cuối (nếu file nhỏ hơn thì lấy hết)
  const headBlob = file.slice(0, 64 * 1024);
  const tailBlob = file.slice(Math.max(file.size - 64 * 1024, 0));

  const [headBuf, tailBuf] = await Promise.all([
    headBlob.arrayBuffer(),
    tailBlob.arrayBuffer(),
  ]);

  // Gộp meta + head + tail
  const combined = new Uint8Array([
    ...encoder.encode(meta),
    ...new Uint8Array(headBuf),
    ...new Uint8Array(tailBuf),
  ]);

  // 🛠️ Fallback cho trình duyệt cũ
  const base64 = btoa(
      String.fromCharCode(...combined.slice(0, 256)) // chỉ dùng 256 bytes đầu
  );
  return `${legacyHash(meta + base64)}`;
}


/**
 * Get video ID info for debugging
 */
export function getVideoIdInfo(file: File, videoId: string): {
  method: string;
  fileSize: string;
  safety: string;
  uniqueness: string;
} {
  const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
  
  return {
    method: videoId.includes('-') ? 'Safe Generation' : 'Fallback',
    fileSize: `${fileSizeMB} MB`,
    safety: 'High (no file content reading)',
    uniqueness: 'High (SHA-256 + timestamp + random)'
  };
}
